"use client"

import { ComponentConfig } from "@measured/puck"
import { useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import FeedbackIcon from "../../../../../../../public/images/feedback-icon.png"
import { configs } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"
import "./CustomFeedback.css"
export interface FeedbackItem {
  textField: {
    title?: string
    titleFontSize?: string
    titleFontWeight?: string
    titleLineHeight?: string
    content?: string
    contentTextSize?: string
    contentTextWeight?: string
    contentLineHeight?: string
  }
  textContainerStyles?: {
    position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center"
    color?: string
    padding?: string
    gap?: string
  }
  customerStyles?: {
    avatar?: string
    name?: string
    nameFontSize?: string
    nameFontWeight?: string
    nameLineHeight?: string
    location?: string
    locationFontSize?: string
    locationFontWeight?: string
    locationLineHeight?: string
    widthAvatar?: string
    heightAvatar?: string
    margin?: string
  }
}

export interface FeedbackProps extends CommonStylesProps {
  heightFeedback?: string
  gapFeedback?: string
  feedbackItems?: FeedbackItem[]
  type: "fluid" | "custom" | "boxed"
}

export const RenderConfig: ComponentConfig<FeedbackProps> = {
  render: ({ className, type, styles, responsiveType, heightFeedback, gapFeedback, feedbackItems = [] }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    const sliderRef = useRef<HTMLDivElement>(null)
    const [currentIndex, setCurrentIndex] = useState(0)

    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!sliderRef.current) return

      e.preventDefault()

      setIsDragging(true)
      setStartX(e.pageX - sliderRef.current.offsetLeft)
      setScrollLeft(sliderRef.current.scrollLeft)

      sliderRef.current.style.cursor = "grabbing"
      sliderRef.current.style.userSelect = "none"
    }
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !sliderRef.current) return

      const x = e.pageX - sliderRef.current.offsetLeft
      const walk = (x - startX) * 2
      sliderRef.current.scrollLeft = scrollLeft - walk
    }

    const handleMouseUp = () => {
      if (!sliderRef.current) return

      setIsDragging(false)
      sliderRef.current.style.cursor = "grab"
      sliderRef.current.style.userSelect = "auto"
    }
    const handleMouseLeave = () => {
      if (!sliderRef.current) return

      setIsDragging(false)
      sliderRef.current.style.cursor = "grab"
      sliderRef.current.style.userSelect = "auto"
    }
    const handleScroll = () => {
      if (!sliderRef.current) return
      const itemWidth = sliderRef.current.scrollWidth / feedbackItems.length
      const newIndex = Math.round(sliderRef.current.scrollLeft / itemWidth)
      setCurrentIndex(newIndex)
    }
    useEffect(() => {
      const slider = sliderRef.current
      if (slider) {
        slider.addEventListener("scroll", handleScroll)
        return () => slider.removeEventListener("scroll", handleScroll)
      }
    }, [])

    return (
      <>
        {responsiveCSS}
        <div id={id} className="relative w-full">
          <div
            ref={sliderRef}
            className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbarcustom-hidden scroll-smooth"
            style={{
              height: heightFeedback || "auto",
              gap: gapFeedback || "24px",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {feedbackItems.map((item, index) => (
              <div
                key={index}
                className="p-6 border border-[#F5F5F5] rounded-lg lg:w-[454px] w-[343px] bg-[#F2F2F780] flex flex-col items-start snap-start flex-shrink-0 relative"
              >
                <img src={FeedbackIcon} alt="Feedback Icon" className="mb-6 w-[28.8px] h-6" />
                <h3
                  className="mb-2"
                  style={{
                    color: item.textContainerStyles?.color,
                    fontSize: item.textField?.titleFontSize,
                    fontWeight: item.textField?.titleFontWeight,
                    lineHeight: item.textField?.titleLineHeight,
                  }}
                >
                  {item.textField?.title}
                </h3>
                <p
                  className="lg:w-full w-[295px]"
                  style={{
                    color: item.textContainerStyles?.color,
                    fontSize: item.textField?.contentTextSize,
                    fontWeight: item.textField?.contentTextWeight,
                    lineHeight: item.textField?.contentLineHeight,
                  }}
                >
                  {item.textField?.content}
                </p>
                <div className="flex items-center gap-2 mt-6">
                  <img
                    src={`${configs.API_URL}${item.customerStyles?.avatar}`}
                    alt="Avatar"
                    className="rounded-full"
                    style={{
                      width: item.customerStyles?.widthAvatar || "40px",
                      height: item.customerStyles?.heightAvatar || "40px",
                      margin: item.customerStyles?.margin || "0",
                    }}
                  />
                  <div className="flex flex-col gap-1">
                    <p
                      style={{
                        fontSize: item.customerStyles?.nameFontSize || "16px",
                        fontWeight: item.customerStyles?.nameFontWeight || 700,
                        lineHeight: item.customerStyles?.nameLineHeight || "19px",
                      }}
                    >
                      {item.customerStyles?.name}
                    </p>
                    <p
                      style={{
                        fontSize: item.customerStyles?.locationFontSize || "14px",
                        fontWeight: item.customerStyles?.locationFontWeight || 400,
                        lineHeight: item.customerStyles?.locationLineHeight || "16px",
                      }}
                    >
                      {item.customerStyles?.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center w-full justify-center lg:hidden">
          <div className="flex justify-center mt-4 bg-[#242F3E0F] py-[10px] px-2 rounded-full">
            {feedbackItems.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-2 mx-[5px] rounded-full transition-all ${
                  currentIndex === index ? "bg-[#000] w-6" : "bg-[#D9D9D9]"
                }`}
              />
            ))}
          </div>
        </div>
      </>
    )
  },
}
